import { useSnackbar } from 'notistack';
import { noCase } from 'change-case';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'jalali-moment';
// @mui
import {
  Box,
  List,
  Badge,
  Avatar,
  Divider,
  Typography,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
} from '@mui/material';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import { fCurrency } from '../../../utils/formatNumber';
import { fetchWithdrawalList } from '../../../api/withdrawal';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { fetchTickets } from '../../../api/ticket';
import { fetchUserRequestList } from '../../../api/user';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const { enqueueSnackbar } = useSnackbar();
  const [notifications, setNotifications] = useState([]);

  const lastReloadTime = useSelector((state) => state.notification.lastReloadTime);

  const totalUnRead = notifications.filter((item) => item.isUnRead === true).length;

  const [open, setOpen] = useState(null);
  const reloadTimer = useRef();

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const getPending = async () => {
    clearTimeout(reloadTimer.current);

    try {
      let list = [];

      // const orders = await fetchOrderList(1, 1000000, { status: 'pending' });
      // list = [
      //   ...list,
      //   ...orders.items.map((d) => ({
      //     title: `سفارش ${fCurrency(d.amount)} ${d.unit.title}`,
      //     link: PATH_DASHBOARD.order.view(d.id),
      //     type: 'order',
      //     createdAt: d.createdAt,
      //     isUnRead: true,
      //   })),
      // ];

      const requests = await fetchUserRequestList(1, 1000000, { status: 'pending', type: 'level' });
      list = [
        ...list,
        ...requests.items.map((d) => ({
          title: `درخواست رفع محدودیت "${d.user.firstName || ''} ${d.user.lastName || ''}"`,
          link: PATH_DASHBOARD.userRequest.view(d.id),
          type: 'upgrade',
          createdAt: d.createdAt,
          isUnRead: true,
        })),
      ];

      const withdraws = await fetchWithdrawalList(1, 1000000, { status: 'pending' });
      list = [
        ...list,
        ...withdraws.items.map((d) => ({
          title: `درخواست برداشت وجه ${fCurrency(d.amount)} ${d.currencyUnit.title}`,
          link: PATH_DASHBOARD.withdrawal.view(d.id),
          type: 'withdraw',
          createdAt: d.createdAt,
          isUnRead: true,
        })),
      ];

      const tickets = await fetchTickets(1, 1000000, { admin_readed: '0' });
      list = [
        ...list,
        ...tickets.items.map((d) => ({
          title: `تیکت " ${d.subject} "`,
          link: PATH_DASHBOARD.ticket.view(d.id),
          type: 'ticket',
          createdAt: d.createdAt,
          isUnRead: true,
        })),
      ];

      setNotifications(list.sort((a, b) => moment(a.createdAt).diff(moment(b.createdAt))));
    } catch (e) {
      console.log(e);
      enqueueSnackbar(e.message || e.messages[0], { variant: 'error' });
    }

    // Getting new data each 5 min
    reloadTimer.current = setTimeout(() => {
      getPending();
    }, 1000 * 60 * 5);
  };

  useEffect(() => {
    getPending();
  }, [lastReloadTime]);

  return (
    <>
      <IconButtonAnimate color={open ? 'primary' : 'default'} onClick={handleOpen} sx={{ width: 40, height: 40 }}>
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="eva:bell-fill" width={20} height={20} />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 360, p: 0, mt: 1.5, mr: 0.75 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">اعلانات</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              شما {totalUnRead} اعلان بررسی نشده دارید
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ height: { xs: 340, sm: 375 } }}>
          <List disablePadding>
            {notifications.map((notification, index) => (
              <NotificationItem key={index} notification={notification} handleClose={handleClose} />
            ))}
          </List>
        </Scrollbar>
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

function NotificationItem({ notification, handleClose }) {
  const navigate = useNavigate();
  const { avatar, title } = renderContent(notification);

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
      }}
      onClick={() => {
        handleClose();
        navigate(notification.link);
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'background.neutral' }}>{avatar}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
            {moment(notification.createdAt).locale('fa').fromNow()}
          </Typography>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification) {
  const title = (
    <Typography variant="subtitle2">
      {notification.title}
      {notification?.description && (
        <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
          &nbsp; {noCase(notification.description)}
        </Typography>
      )}
    </Typography>
  );

  if (notification.type === 'order') {
    return {
      avatar: <Iconify icon="eva:shopping-cart-outline" sx={{ color: 'primary.main' }} />,
      title,
    };
  }

  if (notification.type === 'ticket') {
    return {
      avatar: <Iconify icon="eva:message-square-outline" sx={{ color: 'primary.main' }} />,
      title,
    };
  }

  if (notification.type === 'withdraw') {
    return {
      avatar: <Iconify icon="eva:credit-card-outline" sx={{ color: 'error.dark' }} />,
      title,
    };
  }

  if (notification.type === 'upgrade') {
    return {
      avatar: <Iconify icon="solar:crown-outline" sx={{ color: 'warning.dark' }} />,
      title,
    };
  }

  return {
    avatar: notification.avatar ? <img alt={notification.title} src={notification.avatar} /> : null,
    title,
  };
}
